/**
 * TaxEmailRequestCard (TAX-EMAIL-RECEIVED-001 / TAX-EMAIL-UI-SIMPLIFY-001 / TAX-EMAIL-MULTIUPLOAD-001) — one CURRENT
 * Tax Collector email request per jurisdiction, operational view only:
 *   Jurisdiction | status chip | Received / Resend        Recipient · Sent · By
 *   Received → premium drag & drop panel (up to 5 response documents, all-or-nothing upload) → Response Documents (n).
 *   Everything technical sits under a collapsed "Details".
 */
import React, { useState } from 'react';
import { RefreshCw, Loader2, CheckCircle, XCircle, AlertTriangle, Clock, Download, Upload, Hourglass, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeET } from '../../utils/formatters';
import { markTaxEmailReceived, openOrderFile } from '../../lib/taxEmailApi';
import { responseFiles } from '../../lib/taxEmailStatus';
import { DropZone, FileCard, FileBadge, ClearAllButton, validateSelection, fmtSize } from './TaxEmailResponseUploader';

export const STATUS = {
  sending: { label: 'Sending…', cls: 'bg-blue-100 text-blue-700', Icon: Loader2, spin: true },
  sent: { label: 'Sent — Awaiting Response', cls: 'bg-blue-50 text-blue-700', Icon: Hourglass },
  received: { label: 'Response Received', cls: 'bg-teal-100 text-teal-700', Icon: CheckCircle },
  send_failed: { label: 'Failed', cls: 'bg-rose-100 text-rose-700', Icon: XCircle },
  no_recipient: { label: 'No recipient email', cls: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
  not_configured: { label: 'Automation off', cls: 'bg-slate-100 text-slate-600', Icon: Clock },
  skipped_duplicate: { label: 'Duplicate skipped', cls: 'bg-slate-100 text-slate-600', Icon: Clock },
  blocked_recipient: { label: 'Recipient blocked', cls: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
};
const RETRYABLE = ['sent', 'send_failed', 'no_recipient', 'skipped_duplicate', 'not_configured', 'blocked_recipient'];
export const fmt = (iso) => (iso ? formatDateTimeET(iso) : '—');

const Field = ({ label, children, testId }) => (
  <span className="whitespace-nowrap"><span className="text-slate-400">{label}:</span> <span data-testid={testId}>{children}</span></span>
);

const Details = ({ rec }) => (
  <div className="mt-2 pt-2 border-t border-slate-100 grid grid-cols-1 gap-y-0.5 text-[11px] text-slate-500" data-testid="tax-email-details">
    <span>Sent {fmt(rec.sent_at)} by {rec.sent_by_name || '—'} to <span className="font-mono">{rec.recipient || '—'}</span></span>
    <span>Delivery: {rec.status === 'send_failed' ? 'SES send failed' : rec.message_id ? 'Accepted by SES' : rec.status === 'sending' ? 'Sending…' : '—'}</span>
    {rec.sender && <span>From {rec.from_name ? `${rec.from_name} <${rec.sender}>` : rec.sender}{rec.reply_to ? ` · Reply-To ${rec.reply_to}` : ''}</span>}
    {rec.cc?.length > 0 && <span data-testid="tax-email-cc-used">CC {rec.cc.join(', ')}</span>}
    {rec.cc_dropped?.length > 0 && <span className="text-amber-600" data-testid="tax-email-cc-dropped">CC not sent (domain not allowed in this environment): {rec.cc_dropped.join(', ')}</span>}
    {rec.tax_history_requirement?.label && <span data-testid="tax-email-history-label">Tax history: {rec.tax_history_requirement.label}</span>}
    {rec.subject && <span className="break-words">Subject: {rec.subject}</span>}
    {rec.message_id && <span className="font-mono break-all">SES Message ID: {rec.message_id}</span>}
    {rec.resend_of && <span>Resend of an earlier request</span>}
    <span>Recorded: {fmt(rec.created_at)}</span>
  </div>
);

/** Response Documents (n) — each opened through the existing permission-checked /files/{id}/download. */
const ResponseDocuments = ({ rec, orderId }) => {
  const docs = responseFiles(rec);
  if (!docs.length) return null;
  const view = (id) => openOrderFile(orderId, id).catch((e) => toast.error(e.message));
  return (
    <div className="mt-2 rounded-xl border border-teal-200 bg-teal-50/40 p-3" data-testid="tax-email-response-documents">
      <p className="text-sm font-semibold text-slate-800 mb-2" data-testid="tax-email-response-documents-title">Response Documents ({docs.length})</p>
      <div className="space-y-1.5">
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2.5 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5" data-testid="tax-email-response-document">
            <FileBadge name={d.filename} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-slate-800 truncate" title={d.filename}>{d.filename}</p>
              {d.file_size ? <p className="text-[11px] text-slate-500">{fmtSize(d.file_size)}</p> : null}
            </div>
            <button onClick={() => view(d.id)} data-testid="tax-email-view-response-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-teal-300 bg-teal-50 hover:bg-teal-100 text-teal-700 transition-colors">
              <Eye className="w-3 h-3" /> View
            </button>
            <button onClick={() => view(d.id)} data-testid="tax-email-download-response-btn"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 transition-colors">
              <Download className="w-3 h-3" /> Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function TaxEmailRequestCard({ rec, orderId, canAction, busy, onResend, onReceived }) {
  const [showUpload, setShowUpload] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | uploading | done | failed
  const [progress, setProgress] = useState(0);
  const [failure, setFailure] = useState(null);
  const s = STATUS[rec.status] || STATUS.not_configured;
  const isReceived = rec.status === 'received';
  const isFailure = !['sending', 'sent', 'received'].includes(rec.status);
  const canReceive = canAction && ['sent', 'send_failed'].includes(rec.status);
  const canResend = canAction && !isReceived && RETRYABLE.includes(rec.status);
  const uploading = phase === 'uploading';
  const total = files.reduce((n, f) => n + f.size, 0);

  const addFiles = (incoming) => {
    if (!incoming.length || uploading) return;
    const { accepted, rejected } = validateSelection(files, incoming);
    rejected.forEach((r) => toast.error(`${r.name}: ${r.reason}`));
    setFiles(accepted);
    setFailure(null);
    if (phase !== 'idle') setPhase('idle');
  };
  const removeFile = (i) => { setFiles((l) => l.filter((_, idx) => idx !== i)); setFailure(null); };
  const cancel = () => { if (uploading) return; setShowUpload(false); setFiles([]); setPhase('idle'); setProgress(0); setFailure(null); };
  const upload = async () => {
    if (!files.length || uploading) return;
    setPhase('uploading'); setProgress(0); setFailure(null);
    try {
      const data = await markTaxEmailReceived(orderId, rec.id, files, setProgress);
      setProgress(100); setPhase('done');
      toast.success(data.message || 'Email response marked received');
      setTimeout(() => { setShowUpload(false); setFiles([]); setPhase('idle'); onReceived && onReceived(data); }, 900);
    } catch (e) {
      setPhase('failed');
      setFailure({ file: e.failedFile, message: e.message });
      toast.error(e.message);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl px-4 py-3 bg-white shadow-sm" data-testid={`tax-email-request-${rec.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-bold tracking-wide text-slate-800 truncate uppercase" data-testid="tax-email-jurisdiction">{rec.jurisdiction_name}</span>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${s.cls}`} data-testid="tax-email-status">
            <s.Icon className={`w-3.5 h-3.5 ${s.spin ? 'animate-spin' : ''}`} /> {s.label}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canReceive && (
            <button onClick={() => setShowUpload((v) => !v)} data-testid="tax-email-received-button" aria-expanded={showUpload}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg border transition-colors
                ${showUpload ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-emerald-500 bg-white hover:bg-emerald-50 text-emerald-700'}`}>
              <Download className="w-4 h-4" /> Received
            </button>
          )}
          {canResend && (
            <button onClick={() => onResend(rec)} disabled={busy} data-testid="tax-email-resend-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 transition-colors">
              <RefreshCw className="w-4 h-4" /> {rec.status === 'sent' ? 'Resend' : 'Retry send'}
            </button>
          )}
        </div>
      </div>

      <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${showUpload ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'}`}
        data-testid="tax-email-received-panel" data-open={showUpload ? 'true' : 'false'} aria-hidden={!showUpload}>
        <div className="overflow-hidden">
          {showUpload && (
            <div className="space-y-3" data-testid="tax-email-received-upload-area">
              <DropZone disabled={uploading} onFiles={addFiles} count={files.length} />
              {files.length > 0 && (
                <div className="space-y-2" data-testid="tax-email-selected-files">
                  <div className="flex items-center gap-4 px-1">
                    <p className="text-sm font-semibold text-slate-800" data-testid="tax-email-selected-count">Selected Files ({files.length})</p>
                    <ClearAllButton onClick={() => { setFiles([]); setFailure(null); setPhase('idle'); }} disabled={uploading} />
                    <span className="ml-auto text-xs text-slate-500" data-testid="tax-email-selected-total">{fmtSize(total)} of 25 MB</span>
                  </div>
                  {files.map((f, i) => (
                    <FileCard key={`${f.name}-${f.size}`} file={f} progress={progress} disabled={uploading}
                      state={phase === 'uploading' ? 'uploading' : phase === 'done' ? 'done' : phase === 'failed' ? (failure?.file && failure.file !== f.name ? 'ready' : 'failed') : 'ready'}
                      failedReason={phase === 'failed' && (!failure?.file || failure.file === f.name) ? (failure?.file ? 'Could not be stored' : undefined) : undefined}
                      onRemove={() => removeFile(i)} />
                  ))}
                </div>
              )}
              {phase === 'failed' && failure && (
                <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="tax-email-upload-error">
                  {failure.message} Nothing was saved and the request is still awaiting response.
                </p>
              )}
              <div className="flex items-center justify-end gap-3 pt-1">
                <button onClick={cancel} disabled={uploading} data-testid="tax-email-received-cancel-btn"
                  className="px-5 py-2.5 text-sm font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 transition-colors">Cancel</button>
                <button onClick={upload} disabled={!files.length || uploading || phase === 'done'} data-testid="tax-email-received-upload-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99] shadow-md transition-[background-color,transform] disabled:opacity-50 disabled:shadow-none">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : phase === 'done' ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                  {uploading ? `Uploading… ${progress}%` : phase === 'done' ? 'Received' : `Upload & Mark Received${files.length ? ` (${files.length} File${files.length > 1 ? 's' : ''})` : ''}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {isReceived && <ResponseDocuments rec={rec} orderId={orderId} />}

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-slate-600">
        {isReceived ? (
          <span data-testid="tax-email-received-row">
            <span className="text-teal-600">Received:</span> <span data-testid="tax-email-received-at">{fmt(rec.received_at)}</span>
            {rec.received_by_name && <span className="text-slate-400"> by {rec.received_by_name}</span>}
          </span>
        ) : (
          <>
            <Field label="Recipient" testId="tax-email-recipient"><span className="font-mono">{rec.recipient || '—'}</span></Field>
            <Field label="Sent" testId="tax-email-sent-at">{fmt(rec.sent_at)}</Field>
            <Field label="By" testId="tax-email-sent-by">{rec.sent_by_name || '—'}</Field>
          </>
        )}
        <button onClick={() => setShowDetails((v) => !v)} data-testid="tax-email-details-toggle"
          className="ml-auto inline-flex items-center gap-0.5 text-[11px] text-slate-400 hover:text-slate-600">
          {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Details
        </button>
      </div>
      {isFailure && rec.error && <p className="mt-1 text-xs text-rose-600" data-testid="tax-email-error">{rec.error}</p>}
      {showDetails && <Details rec={rec} />}
    </div>
  );
}
